"use client";

import React, { Suspense, useState } from "react";
import { HiOutlineLocationMarker } from "react-icons/hi";
import classNames from "classnames";
import dynamic from "next/dynamic";
import get from "lodash/get";

// components
import SearchMap from "@/components/SearchMap";
import LocationCard from "@/components/LocationCard";
import SearchSpacesDrawer from "@/containers/SearchSpacesDrawer";
const ChatDrawer = dynamic(() => import("@/containers/ChatDrawer"), { ssr: false });

// helpers
import useUserProfile from "@/hooks/useUserProfile";
import queryString from "@/helpers/queryString";
import spaces from "@/api/query/spaces";

// interfaces
import { ISearchResultsPageCards } from "../ISearchResultsPageComponent";
import { ISingleSearch } from "@/types/api/SingleSearchResponse";
import { ISingleSpaceItem } from "@/types/api/SingleSpaceItem";

// assets
import styles from "@/assets/styles/pages/search-results.module.scss";

const SearchResultsPageCards = (props: ISearchResultsPageCards) => {
  const { user, isFetching, resultData, currency, showMap, purpose } = props;

  const [activeLocationId, setActiveLocationId] = useState<string | number | null>(null);
  const [spacesDrawerOpened, setSpacesDrawerOpened] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<ISingleSearch | Record<string, never>>({});
  const [messageDrawerState, setMessageDrawerState] = useState<{
    isOpen: boolean,
    spaceData: ISingleSpaceItem | null
  }
  >({
    isOpen: false,
    spaceData: null,
  });

  const { isAuthorized } = useUserProfile();


  const { refetch } = spaces.useSpacesList({
    params: {
      ...queryString.parse(currentLocation.spaceUrl),
      limit: 100,
      ordering: "position",
      analytics: true
    },
    config: {
      enabled: false,
    },
  });


  const initialMapCenter = {
    lat: resultData?.results ? resultData?.results[0]?.latitude : 0,
    lng: resultData?.results ? resultData?.results[0]?.longitude : 0,
  };

  const locationToShowOnMap = (get(resultData, "results") || []);

  // methods
  const onSpacesCardClick = (location: ISingleSearch) => {
    setSpacesDrawerOpened(true);
    setCurrentLocation(location);
  };

  const onDrawerClose = () => {
    setSpacesDrawerOpened(false);
    setCurrentLocation({});
  };

  // renders
  const resultsWrapperClass = classNames(
    styles["locl-search-results__cards__wrapper__item"],
    { [styles["locl-search-results__cards__wrapper__item__empty-wrap"]]: !isFetching && !resultData?.results?.length }
  );

  const renderResults = () => {
    switch (true) {
      case !resultData?.results?.length && !isFetching:
        return (
          <div className={styles["locl-search-results__cards__empty"]} data-testid="locl-search-results-empty-state">
            <div className={styles["locl-search-results__cards__empty__content"]} >
              <HiOutlineLocationMarker/>
              <p>Please run a new search</p>
            </div>
          </div>
        );
      case !!resultData?.results?.length:
        return get(resultData, "results", []).map(item => (
          <LocationCard
            user={user}
            onSpacesClick={() => onSpacesCardClick(item)}
            location={item}
            key={item.name}
            currency={currency}
            setActiveLocationId={setActiveLocationId}
            purpose={purpose}
          />
        ));
      default:
        return Array.from({ length: 8 }).fill(null).map((_, index) => <LocationCard.Loading key={index}/>);
    }
  };


  return (
    <div className={styles["locl-search-results__cards"]}>
      <div className={styles["locl-search-results__cards__wrapper"]}>
        <div className={resultsWrapperClass} data-testid="locl-search-results-wrapper">
          { renderResults() }
        </div>
        {showMap && !!resultData?.results?.length && (
          <div className={styles["locl-search-results__cards__wrapper__map"]}>
            <SearchMap
              locations={locationToShowOnMap}
              defaultCenter={initialMapCenter}
              activeLocationId={activeLocationId as string}
            />
          </div>
        )}
        <SearchSpacesDrawer
          onClose={onDrawerClose}
          currency={currency?.code}
          open={spacesDrawerOpened}
          locationName={currentLocation.name}
          spacesSearchLink={currentLocation.spaceUrl}
          ordering={currentLocation.spacesOrdering}
          spaceCount={currentLocation.spaceCount}
          isShowMessageButton={isAuthorized}
          onMessageButtonClick={(spaceData) => setMessageDrawerState({ isOpen: true, spaceData })}
        />
        {
          <Suspense fallback="...">
            <ChatDrawer
              open={messageDrawerState.isOpen}
              onClose={() => {
                setMessageDrawerState({ isOpen: false, spaceData: null });
                refetch();
              }}
              spaceId={messageDrawerState.spaceData?.id as number}
              spaceCampaigns={messageDrawerState.spaceData?.campaigns || []}
              locationName={messageDrawerState.spaceData?.location.name as string}
              spaceName={messageDrawerState.spaceData?.name as string}
              spaceSlug={messageDrawerState.spaceData?.slug as string}
              locationSlug={messageDrawerState.spaceData?.location.slug as string}
              zIndex={99999}
            />
          </Suspense>
        }
      </div>
    </div>
  );
};

export default SearchResultsPageCards;
